import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import MetaHead from "./MetaHead";
import { useTranslation } from "react-i18next";
import { MonthViewProps } from "../types";
import {
  getMonthDates,
  isDateInMonth,
  formatDateToString,
  getMonthYearDisplay,
  isToday,
} from "../utils/dateUtils";
import {
  calculateDateTileDataFromState,
  selectCalendarDayViewData,
} from "../utils/dataUtils";
import { useAppSelector } from "../store/hooks";
import DateTile from "./DateTile";
import AppSidebar from "./AppSidebar";
import { useTheme } from "../hooks/useTheme";
import { trackPageVisit } from "../utils/analytics";
import { StreakSparkIcon } from "./svg-icons/streak-spark";
import { GLOBAL_FONT_SIZE_OFFSET } from "../config/constants";

// Day-of-week single-letter labels (Sun Mon ... Sat)
const DOW_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

// User initials helper
const getInitials = (
  name?: string | null,
  username?: string | null,
): string => {
  if (name) {
    return name
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  }
  if (username) return username.charAt(0).toUpperCase();
  return "U";
};

const frontOffset = GLOBAL_FONT_SIZE_OFFSET;

const MonthView: React.FC<MonthViewProps> = ({
  currentDate,
  onDateSelect,
  onNavigateMonth,
  onHeaderClick,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isDark, setTheme } = useTheme();

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    trackPageVisit("month_calendar", {
      month: currentDate.getMonth() + 1,
      year: currentDate.getFullYear(),
    });
  }, [currentDate]);

  // Redux state
  const calendarData = useAppSelector(selectCalendarDayViewData);
  const streakSpark = useAppSelector((s) => s.user.streakSpark);
  const userProfile = useAppSelector((s) => s.user.profile);
  const habits = useAppSelector((s) => s.habits.items);

  // Active habit filter pill (visual only)
  const [activeFilter, setActiveFilter] = useState<string>("all");

  // Generate calendar date tiles
  const monthDates = useMemo(() => getMonthDates(year, month), [year, month]);

  const dateData = useMemo(() => {
    return monthDates.map((date) => {
      const dateString = formatDateToString(date);
      const isCurrentMonth = isDateInMonth(date, month, year);
      const isTodayDate = isToday(dateString);
      return calculateDateTileDataFromState(
        calendarData,
        dateString,
        isCurrentMonth,
        isTodayDate,
      );
    });
  }, [monthDates, year, month, calendarData]);

  // Stats derived from current month data
  const stats = useMemo(() => {
    const monthData = dateData.filter((d) => d.isCurrentMonth);
    const wins = monthData.filter((d) => d.isBingo).length;
    const totalHabits = monthData.reduce((s, d) => s + d.habitsCount, 0);
    const completedHabits = monthData.reduce(
      (s, d) => s + d.completedHabitsCount,
      0,
    );
    const donePct =
      totalHabits > 0 ? Math.round((completedHabits / totalHabits) * 100) : 0;
    return {
      streak: streakSpark?.currentStreak ?? 0,
      wins,
      donePct,
      best: streakSpark?.longestStreak ?? 0,
    };
  }, [dateData, streakSpark]);

  const initials = getInitials(userProfile?.name, userProfile?.username);
  const monthYearDisplay = getMonthYearDisplay(currentDate);

  const handleThemeToggle = () => {
    setTheme(isDark ? "light" : "dark");
  };

  const isNavActive = useCallback(
    (path: string) =>
      location.pathname === path || location.pathname.startsWith(path + "/"),
    [location.pathname],
  );

  const mobileNavLinks = [
    { path: "/app", emoji: "\u{1F4C5}", label: t("nav.calendar") },
    { path: "/habits", emoji: "\u{1F525}", label: t("nav.habits") },
    { path: "/todos", emoji: "\u2705", label: t("nav.todos") },
    { path: "/trackers", emoji: "\u{1F4CA}", label: t("nav.trackers") },
    { path: "/notes", emoji: "\u{1F4DD}", label: t("nav.notes") },
  ];

  return (
    <>
      <MetaHead
        title={"Home"}
        description={t("calendar.description") as string}
        keywords={t("calendar.keywords") as string}
      />

      {/* Root: full-page layout that overrides App.tsx gradient */}
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex flex-col lg:flex-row lg:max-w-6xl lg:mx-auto">
        {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
            DESKTOP SIDEBAR (lg+)
        â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
        <AppSidebar />

        {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
            MAIN CONTENT AREA
        â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
        <main className="flex-1 flex flex-col bg-[#f7f6f3] dark:bg-[#0f0f12] min-h-screen lg:min-h-0 pb-24 lg:pb-0">
          {/* â”€â”€ MOBILE TOPBAR (hidden on lg) â”€â”€ */}
          <header className="lg:hidden flex items-center justify-between px-4 py-4 border-b border-black/[0.07] dark:border-white/[0.07] sticky top-0 bg-[#f7f6f3] dark:bg-[#0f0f12] z-20">
            <div className="flex items-center gap-2.5">
              <button
                onClick={() => navigate("/")}
                aria-label="Go to home page"
                className="w-8 h-8 rounded-[9px] bg-[#f5a623] flex items-center justify-center text-base"
              >
                <StreakSparkIcon />
              </button>
              <span className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white">
                {t("app.name")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleThemeToggle}
                className="w-8 h-8 rounded-[9px] border border-black/10 dark:border-white/10 bg-black/[0.04] dark:bg-white/[0.05] text-gray-500 dark:text-white/50 text-[19px] flex items-center justify-center cursor-pointer transition-all hover:bg-black/10 dark:hover:bg-white/10"
                aria-label={
                  isDark
                    ? (t("theme.switchToLight") as string)
                    : (t("theme.switchToDark") as string)
                }
              >
                {isDark ? "\u2600\uFE0F" : "\u{1F319}"}
              </button>
              <button
                onClick={() => navigate("/profile")}
                className="w-8 h-8 rounded-full bg-[#3b5cde] flex items-center justify-center text-[16px] font-semibold text-white"
                aria-label="Go to profile"
              >
                {initials}
              </button>
            </div>
          </header>

          {/* â”€â”€ DESKTOP PAGE HEADER â”€â”€ */}
          <div className="hidden lg:flex items-center justify-between px-6 py-5 border-b border-black/[0.07] dark:border-white/[0.07]">
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
                Home
              </h1>
              <p className="text-xs text-gray-400 dark:text-white/30 mt-0.5">
                {t("calendar.subtitle")}
              </p>
            </div>
            {/* Month nav */}
            <div className="flex items-center gap-2 bg-black/[0.04] dark:bg-white/[0.05] rounded-xl px-3 py-2 border border-black/[0.07] dark:border-white/[0.08]">
              <button
                onClick={() => onNavigateMonth("prev")}
                aria-label="Previous month"
                className="text-gray-400 dark:text-white/40 hover:text-gray-900 dark:hover:text-white text-lg leading-none cursor-pointer transition-colors px-1"
              >
                {"<"}
              </button>
              <span
                onClick={onHeaderClick}
                className="text-[19px] font-medium text-gray-800 dark:text-white min-w-[110px] text-center cursor-pointer"
              >
                {monthYearDisplay}
              </span>
              <button
                onClick={() => onNavigateMonth("next")}
                aria-label="Next month"
                className="text-gray-400 dark:text-white/40 hover:text-gray-900 dark:hover:text-white text-lg leading-none cursor-pointer transition-colors px-1"
              >
                {">"}
              </button>
            </div>
          </div>

          {/* â”€â”€ STATS ROW â”€â”€ */}
          <div className="grid grid-cols-4 gap-2 px-3 py-3 lg:px-6 lg:py-4 lg:gap-3">
            {[
              {
                emoji: "\u{1F525}",
                value: stats.streak,
                label: t("calendar.statsStreak"),
              },
              {
                emoji: "\u{1F3C6}",
                value: stats.wins,
                label: t("calendar.statsWins"),
              },
              {
                emoji: "\u2705",
                value: `${stats.donePct}%`,
                label: t("calendar.statsDone"),
              },
              {
                emoji: "\u2B50",
                value: stats.best,
                label: t("calendar.statsBest"),
              },
            ].map(({ emoji, value, label }) => (
              <div
                key={label}
                className="rounded-xl border border-black/[0.07] dark:border-white/[0.07] bg-white dark:bg-white/[0.05] p-3 lg:p-4 text-center"
              >
                <span className="text-base lg:text-xl block mb-1">{emoji}</span>
                <span
                  className={`text-[${19 + frontOffset}px] lg:text-2xl font-semibold tracking-tight text-gray-900 dark:text-white block`}
                >
                  {value}
                </span>
                <span className="text-[15px] lg:text-[16px] uppercase tracking-wider text-gray-400 dark:text-white/30 block mt-0.5">
                  {label}
                </span>
              </div>
            ))}
          </div>

          <hr className="mt-1 mb-1 mx-4 content-center opacity-30" />

          {/* â”€â”€ MOBILE MONTH NAV â”€â”€ */}
          <div className="lg:hidden flex items-center justify-between px-4 pb-1 pt-0 mt-2">
            <span
              onClick={onHeaderClick}
              className={`text-[${19 + frontOffset}px] font-semibold tracking-tight text-gray-900 dark:text-white cursor-pointer`}
            >
              {monthYearDisplay}
            </span>
            <div className="flex gap-1.5">
              <button
                onClick={() => onNavigateMonth("prev")}
                aria-label="Previous month"
                className="w-8 h-8 rounded-[9px] border border-black/10 dark:border-white/10 text-gray-400 dark:text-white/40 text-base flex items-center justify-center cursor-pointer hover:bg-black/[0.05] dark:hover:bg-white/[0.07] transition-colors"
              >
                {"<"}
              </button>
              <button
                onClick={() => onNavigateMonth("next")}
                aria-label="Next month"
                className="w-8 h-8 rounded-[9px] border border-black/10 dark:border-white/10 text-gray-400 dark:text-white/40 text-base flex items-center justify-center cursor-pointer hover:bg-black/[0.05] dark:hover:bg-white/[0.07] transition-colors"
              >
                {">"}
              </button>
            </div>
          </div>

          {/* â”€â”€ HABIT FILTER PILLS â”€â”€ */}
          {/* Tags section hidden temporarily */}
          {false && habits.length > 0 && (
            <div className="pill-row px-3 lg:px-6 py-2.5">
              {/* All pill */}
              <button
                onClick={() => setActiveFilter("all")}
                className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[17px] lg:text-[18px] font-medium cursor-pointer transition-all whitespace-nowrap${
                  activeFilter === "all"
                    ? " bg-[#f5a623]/[0.14] border-[#f5a623]/[0.42] text-[#f5a623]"
                    : " border-black/[0.1] dark:border-white/10 text-gray-400 dark:text-white/30 hover:border-black/20 dark:hover:border-white/20"
                }`}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{
                    background:
                      activeFilter === "all" ? "#f5a623" : "currentColor",
                  }}
                />
                {t("calendar.filterAll")}
              </button>
              {/* Per-habit pills (up to 8) */}
              {habits.slice(0, 8).map((habit) => {
                const isActive = activeFilter === habit.id;
                return (
                  <button
                    key={habit.id}
                    onClick={() => setActiveFilter(isActive ? "all" : habit.id)}
                    className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[17px] lg:text-[18px] font-medium cursor-pointer transition-all whitespace-nowrap${
                      isActive
                        ? " bg-[#f5a623]/[0.14] border-[#f5a623]/[0.42] text-[#f5a623]"
                        : " border-black/[0.1] dark:border-white/10 text-gray-400 dark:text-white/30 hover:border-black/20 dark:hover:border-white/20"
                    }`}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{
                        background: isActive ? "#f5a623" : "currentColor",
                      }}
                    />
                    {habit.name}
                  </button>
                );
              })}
            </div>
          )}

          {/* â”€â”€ CALENDAR GRID â”€â”€ */}
          <div className="px-3 lg:px-6 pb-3">
            {/* DOW headers */}
            <div className="grid grid-cols-7 mb-1">
              {DOW_LABELS.map((d, i) => (
                <div
                  key={i}
                  className="text-center text-[15px] lg:text-[16px] font-medium uppercase tracking-wider py-1.5 text-gray-300 dark:text-white/20"
                >
                  {d}
                </div>
              ))}
            </div>
            {/* Day cells */}
            <div className="grid grid-cols-7 gap-[3px] lg:gap-1.5">
              {dateData.map((data) => (
                <DateTile key={data.date} data={data} onClick={onDateSelect} />
              ))}
            </div>
          </div>

          {/* â”€â”€ LEGEND â”€â”€ */}
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 px-4 lg:px-6 py-2 pb-3">
            <div className="flex items-center gap-1.5 text-[15px] lg:text-[16px] text-gray-400 dark:text-white/30">
              <span className="w-2.5 h-2.5 rounded-[3px] bg-[#f5a623]/40 border border-[#f5a623]/30 inline-block" />
              {t("calendar.legendStreak")}
            </div>
            <div className="flex items-center gap-1.5 text-[15px] lg:text-[16px] text-gray-400 dark:text-white/30">
              <span className="w-2.5 h-2.5 rounded-[3px] bg-[#3b5cde]/60 inline-block" />
              {t("calendar.legendToday")}
            </div>
            <div className="text-[15px] lg:text-[16px] text-gray-400 dark:text-white/30">
              {"\u{1F3C6}"} {t("calendar.legendMilestone")}
            </div>
            <div className="text-[15px] lg:text-[16px] text-gray-400 dark:text-white/30">
              {"\u2B50"} {t("calendar.legendStarred")}
            </div>
            <div className="text-[15px] lg:text-[16px] text-gray-400 dark:text-white/30">
              {"\u{1F3AF}"} {t("calendar.legendGoalHit")}
            </div>
          </div>

          {/* â”€â”€ MOBILE QUICK ACTIONS (hidden on lg) â”€â”€ */}
          <div className="lg:hidden border-t border-black/[0.07] dark:border-white/[0.07] pt-1 pb-1 mt-1">
            <p className="text-[15px] font-medium uppercase tracking-widest text-gray-400 dark:text-white/20 px-4 pt-2 pb-1.5">
              {t("quickActions.title")}
            </p>
            <div className="grid grid-cols-5 gap-2 px-3 pb-3">
              {/* Todo */}
              <button
                onClick={() => navigate("/add-todo")}
                className="tap-scale flex flex-col items-center gap-1.5 py-3 rounded-2xl border border-black/[0.07] dark:border-white/[0.08] bg-white dark:bg-white/[0.04] cursor-pointer transition-all hover:bg-black/[0.05] dark:hover:bg-white/[0.09]"
              >
                <div className="w-8 h-8 rounded-[9px] bg-[#3b5cde]/20 flex items-center justify-center">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <rect
                      x="1"
                      y="2"
                      width="14"
                      height="2"
                      rx="1"
                      fill="#378ADD"
                    />
                    <rect
                      x="1"
                      y="7"
                      width="10"
                      height="2"
                      rx="1"
                      fill="#378ADD"
                    />
                    <rect
                      x="1"
                      y="12"
                      width="12"
                      height="2"
                      rx="1"
                      fill="#378ADD"
                    />
                  </svg>
                </div>
                <span className="text-[15px] font-medium text-gray-500 dark:text-white/40">
                  {t("quickActions.todo")}
                </span>
              </button>
              {/* Habit */}
              <button
                onClick={() => navigate("/add-habit")}
                className="tap-scale flex flex-col items-center gap-1.5 py-3 rounded-2xl border border-black/[0.07] dark:border-white/[0.08] bg-white dark:bg-white/[0.04] cursor-pointer transition-all hover:bg-black/[0.05] dark:hover:bg-white/[0.09]"
              >
                <div className="w-8 h-8 rounded-[9px] bg-[#f5a623]/20 flex items-center justify-center">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <circle
                      cx="8"
                      cy="8"
                      r="6"
                      stroke="#f5a623"
                      strokeWidth="2"
                    />
                    <path
                      d="M5 8l2 2 4-4"
                      stroke="#f5a623"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span className="text-[15px] font-medium text-gray-500 dark:text-white/40">
                  {t("quickActions.habit")}
                </span>
              </button>
              {/* Reminder */}
              <button
                onClick={() => navigate("/add-reminder")}
                className="tap-scale flex flex-col items-center gap-1.5 py-3 rounded-2xl border border-black/[0.07] dark:border-white/[0.08] bg-white dark:bg-white/[0.04] cursor-pointer transition-all hover:bg-black/[0.05] dark:hover:bg-white/[0.09]"
              >
                <div className="w-8 h-8 rounded-[9px] bg-[#e23f5d]/20 flex items-center justify-center">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M8 2a4 4 0 0 0-4 4v3l-1 1h10l-1-1V6a4 4 0 0 0-4-4z"
                      fill="#E23F5D"
                    />
                    <rect
                      x="6.5"
                      y="12"
                      width="3"
                      height="1.5"
                      rx="0.75"
                      fill="#E23F5D"
                    />
                  </svg>
                </div>
                <span className="text-[15px] font-medium text-gray-500 dark:text-white/40">
                  {t("quickActions.remind")}
                </span>
              </button>
              {/* Note */}
              <button
                onClick={() => navigate("/add-note")}
                className="tap-scale flex flex-col items-center gap-1.5 py-3 rounded-2xl border border-black/[0.07] dark:border-white/[0.08] bg-white dark:bg-white/[0.04] cursor-pointer transition-all hover:bg-black/[0.05] dark:hover:bg-white/[0.09]"
              >
                <div className="w-8 h-8 rounded-[9px] bg-[#22c67a]/20 flex items-center justify-center">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <rect
                      x="2"
                      y="1"
                      width="12"
                      height="14"
                      rx="2"
                      stroke="#22c67a"
                      strokeWidth="1.8"
                    />
                    <path
                      d="M5 5h6M5 8h4M5 11h5"
                      stroke="#22c67a"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <span className="text-[15px] font-medium text-gray-500 dark:text-white/40">
                  {t("quickActions.note")}
                </span>
              </button>
              {/* Tracker */}
              <button
                onClick={() => navigate("/add-tracker")}
                className="tap-scale flex flex-col items-center gap-1.5 py-3 rounded-2xl border border-black/[0.07] dark:border-white/[0.08] bg-white dark:bg-white/[0.04] cursor-pointer transition-all hover:bg-black/[0.05] dark:hover:bg-white/[0.09]"
              >
                <div className="w-8 h-8 rounded-[9px] bg-[#a33fe8]/20 flex items-center justify-center">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M2 12l3-4 3 2 3-5 3 3"
                      stroke="#a33fe8"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span className="text-[15px] font-medium text-gray-500 dark:text-white/40">
                  {t("quickActions.track")}
                </span>
              </button>
            </div>
          </div>
        </main>

        {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
            MOBILE BOTTOM NAV (hidden on lg)
        â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#f7f6f3]/95 dark:bg-[#0f0f12]/95 backdrop-blur-sm border-t border-black/[0.08] dark:border-white/[0.08]">
          <div className="flex items-center justify-around px-2 pt-2 pb-2">
            {mobileNavLinks.map(({ path, emoji, label }) => {
              const active = isNavActive(path);
              return (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  className="flex flex-col items-center gap-1 cursor-pointer"
                  style={{ color: active ? "#f5a623" : undefined }}
                >
                  <span className={`text-[${18 + frontOffset}px]`}>
                    {emoji}
                  </span>
                  <span
                    className={`text-[15px] font-medium${active ? "" : " text-gray-400 dark:text-white/25"}`}
                    style={active ? { color: "#f5a623" } : undefined}
                  >
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
      </div>
    </>
  );
};

export default MonthView;
